import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, Input, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import message from "@/utils/globalMessage";
import { formatServiceRequestRef } from "@/utils/entity-ref";
import PageHeader from "@components/Table/PageHeader";
import ResizableProTable from "@components/Table/ResizableProTable";
import ResponsiveSelect from "@components/ResponsiveSelect";
import {
  SERVICE_REQUEST_PRIORITIES,
  SERVICE_REQUEST_STATUS_OPTIONS,
  SERVICE_REQUEST_STATUS_COLORS,
  SERVICE_REQUEST_STATUS_LABELS,
  SERVICE_REQUEST_BILLABLE_OPTIONS,
  normalizeServiceRequestResponse,
} from "./constants";
import ServiceRequestCharts from "./ServiceRequestCharts";
import {
  BillableCell,
  ListPage,
  MobileCardDate,
  MobileCardHeader,
  MobileCardMetric,
  MobileCardMetricLabel,
  MobileCardMetricValue,
  MobileCardMetrics,
  MobileCardOverview,
  MobileCardTitle,
  TableLink,
} from "./ServiceRequestList.styles";

const EMPTY_FILTERS = {
  title: "",
  category: [],
  priority: [],
  status: [],
  billable: "",
};

const getCategoryRows = (response) => {
  const payload = response?.data;
  return Array.isArray(payload) ? payload : payload?.results || [];
};

const getIsBillable = (record) => Boolean(record?.is_billable ?? record?.billable);

const formatCost = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  const amount = Number(value);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "--";
};

const formatModifiedDate = (value) => {
  if (!value) return "--";
  const date = moment(value);
  return date.isValid() ? date.format("YYYY-MM-DD HH:mm") : "--";
};

export default function ServiceRequestList({
  facilityIds = [],
  facilities = [],
  onFacilityChange,
  loadRequests,
  loadCategories,
  getDetailPath,
  createPath,
  showFacilityColumn = false,
  showRequesterColumn = false,
}) {
  const navigate = useNavigate();
  const actionRef = useRef();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [categories, setCategories] = useState([]);
  const [loadedRows, setLoadedRows] = useState([]);

  useEffect(() => {
    if (!loadCategories) return undefined;

    let active = true;
    loadCategories()
      .then((response) => {
        if (active) setCategories(getCategoryRows(response));
      })
      .catch(() => {
        if (active) message.error("Failed to load service request categories");
      });

    return () => {
      active = false;
    };
  }, [loadCategories]);

  const request = useCallback(async (params) => {
    if (!facilityIds.length) {
      setLoadedRows([]);
      return { data: [], total: 0, success: true };
    }
    try {
      const response = await loadRequests(facilityIds, {
        page: params.current,
        page_size: params.pageSize,
        title: filters.title.trim(),
        category: filters.category,
        priority: filters.priority,
        status: filters.status,
        billable: filters.billable,
      });
      const { rows, total } = normalizeServiceRequestResponse(response);
      setLoadedRows(rows);
      return { data: rows, total, success: true };
    } catch (error) {
      setLoadedRows([]);
      message.error("Failed to load service requests");
      return { data: [], total: 0, success: false };
    }
  }, [facilityIds, filters, loadRequests]);

  useEffect(() => {
    setLoadedRows([]);
    actionRef.current?.reload?.();
  }, [facilityIds, filters]);

  const openDetail = useCallback((record) => {
    navigate(getDetailPath(record));
  }, [getDetailPath, navigate]);

  const columns = useMemo(() => [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 100,
      render: (value) => formatServiceRequestRef(value),
    },
    {
      title: "Title / Issue",
      dataIndex: "title",
      key: "title",
      width: 220,
      render: (value, record) => (
        <TableLink
          type="button"
          onClick={() => openDetail(record)}
        >
          {value}
        </TableLink>
      ),
    },
    ...(showFacilityColumn ? [{
      title: "Facility",
      dataIndex: "facility_name",
      key: "facility_name",
      width: 180,
    }] : []),
    { title: "Suite / Area", dataIndex: "suite_area", key: "suite_area", width: 150 },
    { title: "Category", dataIndex: "category_name", key: "category_name", width: 150 },
    { title: "Priority", dataIndex: "priority", key: "priority", width: 110 },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    ...(showRequesterColumn ? [{
      title: "Requested By",
      dataIndex: "createdby_name",
      key: "createdby_name",
      width: 160,
    }] : []),
    {
      title: "Billable",
      dataIndex: "is_billable",
      key: "is_billable",
      width: 105,
      render: (_, record) => {
        const isBillable = getIsBillable(record);
        return <BillableCell $billable={isBillable}>{isBillable ? "Yes" : "No"}</BillableCell>;
      },
    },
    {
      title: "Cost (USD)",
      dataIndex: "quoted_cost",
      key: "quoted_cost",
      width: 115,
      render: (value, record) => formatCost(value ?? record.cost),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 115,
      render: (value) => (
        <Tag color={SERVICE_REQUEST_STATUS_COLORS[value]}>
          {SERVICE_REQUEST_STATUS_LABELS[value] || value}
        </Tag>
      ),
    },
    {
      title: "Modified Date",
      dataIndex: "modified_date",
      key: "modified_date",
      width: 165,
      render: formatModifiedDate,
    },
  ], [openDetail, showFacilityColumn, showRequesterColumn]);

  const updateFilter = useCallback((key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  }, []);

  const toggleFilterValue = useCallback((key, value) => {
    if (value === null || value === undefined) return;
    setFilters((current) => {
      const values = current[key] || [];
      const exists = values.some((item) => String(item) === String(value));
      return {
        ...current,
        [key]: exists
          ? values.filter((item) => String(item) !== String(value))
          : [...values, value],
      };
    });
  }, []);

  const toggleFacility = useCallback((facilityId) => {
    if (!onFacilityChange || facilityId === null || facilityId === undefined) return;

    const exists = facilityIds.some((id) => String(id) === String(facilityId));
    if (exists && facilityIds.length === 1) {
      const allFacilityIds = facilities.map((facility) => facility.id);
      if (allFacilityIds.length > 1) onFacilityChange(allFacilityIds);
      return;
    }

    onFacilityChange([facilityId]);
  }, [facilities, facilityIds, onFacilityChange]);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    onFacilityChange?.([]);
  }, [onFacilityChange]);

  const renderSearches = useCallback(() => (
    <Form className="filter-form service-request-filter-form" autoComplete="off">
      {facilities.length > 0 ? (
        <Form.Item label="Facility" name="facility">
          <ResponsiveSelect
            mode="multiple"
            allowClear
            showSearch
            popupTitle="Select Facilities"
            placeholder="Facility"
            value={facilityIds}
            options={facilities.map((item) => ({ value: item.id, label: item.name }))}
            onChange={(value) => onFacilityChange?.(value || [])}
          />
        </Form.Item>
      ) : null}
      <Form.Item label="Title / Issue" name="title">
        <Input
          allowClear
          placeholder="Search title / issue"
          value={filters.title}
          onChange={(event) => updateFilter("title", event.target.value)}
        />
      </Form.Item>
      <Form.Item label="Category" name="category">
        <ResponsiveSelect
          mode="multiple"
          allowClear
          showSearch
          popupTitle="Select Categories"
          placeholder="Category"
          value={filters.category}
          options={categories.map((category) => ({
            value: category.id,
            label: category.name,
          }))}
          onChange={(value) => updateFilter("category", value || [])}
        />
      </Form.Item>
      <Form.Item label="Priority" name="priority">
        <ResponsiveSelect
          mode="multiple"
          allowClear
          popupTitle="Select Priorities"
          placeholder="Priority"
          value={filters.priority}
          options={SERVICE_REQUEST_PRIORITIES.map((value) => ({ value, label: value }))}
          onChange={(value) => updateFilter("priority", value || [])}
        />
      </Form.Item>
      <Form.Item label="Status" name="status">
        <ResponsiveSelect
          mode="multiple"
          allowClear
          popupTitle="Select Statuses"
          placeholder="Status"
          value={filters.status}
          options={SERVICE_REQUEST_STATUS_OPTIONS}
          onChange={(value) => updateFilter("status", value || [])}
        />
      </Form.Item>
      <Form.Item label="Billable" name="billable">
        <ResponsiveSelect
          allowClear
          popupTitle="Select Billable"
          placeholder="All"
          value={filters.billable || undefined}
          options={SERVICE_REQUEST_BILLABLE_OPTIONS}
          onChange={(value) => updateFilter("billable", value || "")}
        />
      </Form.Item>
    </Form>
  ), [categories, facilities, facilityIds, filters, onFacilityChange, updateFilter]);

  const filterHasValue = Boolean(
    facilityIds.length ||
    filters.title.trim() ||
    filters.category.length ||
    filters.priority.length ||
    filters.status.length ||
    filters.billable
  );

  const pageHeader = (
    <PageHeader
      hide_imports
      hide_search
      hid_new={!createPath}
      new_text="Create Service Request"
      onNew={createPath ? () => navigate(createPath) : undefined}
      handleFresh={() => actionRef.current?.reload?.()}
      renderSearches={renderSearches}
      resetFilters={resetFilters}
      filterHasValue={filterHasValue}
    />
  );

  return (
    <ListPage>
      <ServiceRequestCharts
        rows={loadedRows}
        filters={filters}
        facilityIds={facilityIds}
        onStatusSelect={(value) => toggleFilterValue("status", value)}
        onCategorySelect={(value) => toggleFilterValue("category", value)}
        onFacilitySelect={toggleFacility}
        onPrioritySelect={(value) => toggleFilterValue("priority", value)}
      />
      <ResizableProTable
        actionRef={actionRef}
        rowKey="id"
        columns={columns}
        request={request}
        search={false}
        options={false}
        rowSelection={false}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        scroll={{ x: showFacilityColumn || showRequesterColumn ? 1580 : 1420 }}
        toolBarRender={() => pageHeader}
        mobileCardHeaderRender={(record) => {
          const isBillable = getIsBillable(record);
          return (
            <MobileCardOverview>
              <MobileCardHeader>
                <MobileCardTitle
                  type="button"
                  onClick={() => openDetail(record)}
                >
                  {record.title}
                </MobileCardTitle>
                <MobileCardDate title="Modified Date">
                  {formatModifiedDate(record.modified_date)}
                </MobileCardDate>
              </MobileCardHeader>
              <MobileCardMetrics>
                <MobileCardMetric>
                  <MobileCardMetricLabel>Status</MobileCardMetricLabel>
                  <Tag color={SERVICE_REQUEST_STATUS_COLORS[record.status]}>
                    {SERVICE_REQUEST_STATUS_LABELS[record.status] || record.status}
                  </Tag>
                </MobileCardMetric>
                <MobileCardMetric>
                  <MobileCardMetricLabel>Priority</MobileCardMetricLabel>
                  <MobileCardMetricValue>{record.priority || "--"}</MobileCardMetricValue>
                </MobileCardMetric>
                <MobileCardMetric>
                  <MobileCardMetricLabel>Billable</MobileCardMetricLabel>
                  <BillableCell $billable={isBillable}>{isBillable ? "Yes" : "No"}</BillableCell>
                </MobileCardMetric>
                <MobileCardMetric>
                  <MobileCardMetricLabel>Cost</MobileCardMetricLabel>
                  <MobileCardMetricValue>{formatCost(record.quoted_cost ?? record.cost)}</MobileCardMetricValue>
                </MobileCardMetric>
              </MobileCardMetrics>
            </MobileCardOverview>
          );
        }}
        hideMobileColumnActions
        // The ID column is the first column, so it doubles as the card's title
        // column; re-include it to keep the reference visible in the body.
        mobileExpandedIncludeKeys={["id"]}
        mobileExpandedExcludeKeys={[
          "title",
          "status",
          "priority",
          "is_billable",
          "quoted_cost",
          "modified_date",
        ]}
        locale={{ emptyText: facilityIds.length ? "No service requests" : "Select one or more facilities" }}
      />
    </ListPage>
  );
}
